/*
 * File: c:\Users\j.mazat\Desktop\AutobuyBackOffice\AutobuyBackOffice\src\Router\Router.tsx
 * Project: c:\Users\j.mazat\Desktop\AutobuyBackOffice\AutobuyBackOffice
 * Created Date: Tuesday June 6th 2023
 * Author: Jonathan Mazat
 * -----
 * Last Modified: Monday, 22nd January 2024 10:31:07
 * Modified By: Simon (s.gourlet@autobuy.fr>)
 * -----
 * Copyright (c) 2023 Autobuy SAS
 * -----
 */

import { createBrowserRouter } from "react-router-dom";
import Root from "Content/Root/Root";
import ErrorPage from "Content/Error/ErrorPage/ErrorPage";
import RouterPathResolver from "Config/Router/RouterPathResolver";
import Login from "Content/Login/Login";
import HomePage from "Content/HomePage/HomePage";
import CarService from "Services/Car/CarService";
import { ExtractQueryInObj, InitRequestParams } from "Services/RouterService/RouterService";
import ProtectedRoute from "./ProtectedRoute";
import { useAuth } from "./AuthProvider";
import ServiceGeneral from "Services/ServiceGeneral";
import UserService from "Services/Users/UserService";
import CustomerPage from "Content/User/CustomerPage";
import UserSingle from "Content/User/UserSingle";
import Company from "Content/Company/Company";
import CompanyService from "Services/CompanyService/CompanyService";
import MyCustomerPage from "Content/User/MyCustomerPage";
import MyCompanyPage from "Content/User/MyCompanyPage";
import HomeService from "Services/Home/HomeService";
import Stats from "Content/Stats/Stats";
import CompanyList from "Content/Company/CompanyList/CompanyList";
import CarList from "Content/Car/CarList/CarList";
import Car from "Content/Car/Car";
import SeenCarService from "Services/SeenCar/SeenCar.service";
import PricingService from "Services/Pricing/PricingService";
import OfferTabs from "Content/Offers/OfferTabs/offerTabs";

const Router = () => {
  const { logout } = useAuth();

  return createBrowserRouter([
    {
    path: `${RouterPathResolver.pathEntryPoint.path}`,
    element: <Root children={[]} />,
    errorElement: <ErrorPage />,
    children: [
      {
        path: `${RouterPathResolver.pathLogin.path}`,
        element: <Login />
      },
      {
        path: `${RouterPathResolver.pathEntryPoint.path}`,
        loader: async ({})=> {
          return [
            HomeService.getUsersInfo(logout),
            HomeService.getCarsInfo(logout)
          ];
        },
        element: <ProtectedRoute condition={"isConnected === true"} redirectPath={RouterPathResolver.pathLogin.path}><HomePage /></ProtectedRoute>,
        errorElement: <Login />,
      },
      {
        path: `${RouterPathResolver.pathUser.path}`,
        loader: ({request})=> {
          const myParams = InitRequestParams(request.url);
          return [
            ServiceGeneral.manageAxiosResponse( () => { return UserService.getUsers({...myParams, per_page: 9999, status: "valid", role: "CLIENT"})},() => logout()),
            ServiceGeneral.manageAxiosResponse( () => { return UserService.getUsers({per_page: 999, status: "notValid", role: "CLIENT"})}, () => logout()),
            ServiceGeneral.manageAxiosResponse( () => { return UserService.getUsers({per_page: 999, status: "archived", role: "CLIENT"})}, () => logout())
          ];
        },
        element: <ProtectedRoute condition={"isConnected === true"} redirectPath={RouterPathResolver.pathHome.path}><CustomerPage /></ProtectedRoute>,
        errorElement: <ErrorPage />,
      },
      {
        path: `${RouterPathResolver.pathMyUser.path}`,
        loader: async ({request})=> {
          const myParams = InitRequestParams(request.url);
          myParams.per_page = 9999;
          const valid = await ServiceGeneral.manageAxiosResponse( () => { return UserService.getMyUsers({...myParams, status: "valid"})}, () => logout());
          return valid;
        },
        element: <ProtectedRoute condition={"isConnected === true"} redirectPath={RouterPathResolver.pathHome.path}><MyCustomerPage /></ProtectedRoute>,
        errorElement: <ErrorPage />,
      },
      {
        path: `${RouterPathResolver.pathStats.path}`,
        loader: ({request}) => {
          return ExtractQueryInObj(request);
        },
        element: <ProtectedRoute condition={"isConnected === true"} redirectPath={RouterPathResolver.pathHome.path}><Stats/></ProtectedRoute>,
        errorElement: <ErrorPage />,
      },
      {
        path: `${RouterPathResolver.pathMyCompany.path}`,
        loader: async ({request})=> {
          const myParams = InitRequestParams(request.url);
          const myUsers = await ServiceGeneral.manageAxiosResponse( () => { return UserService.getUsers({...myParams, status: "valid", role: "NOTCUSTOMER", per_page:999, includeRole: true})}, () => logout());
          return myUsers;
        },
        element: <ProtectedRoute condition={"isConnected === true"} redirectPath={RouterPathResolver.pathHome.path}><MyCompanyPage /></ProtectedRoute>,
        errorElement: <ErrorPage />,
      },
      {
        path: `${RouterPathResolver.pathUser_BY_ID.path}`,
        loader: async ({params, request})=> {
          const myParams = InitRequestParams(request.url);
          return Promise.all([
            ServiceGeneral.manageAxiosResponse(() => {return UserService.getUserByID(params.id!)}, () => logout()),
            ServiceGeneral.manageAxiosResponse(() => {return SeenCarService.getAllView({userID: params.id!})}, () => logout())
          ]).then(([userData, seenCars]) => {
            if (userData === null || userData === undefined)
              throw Error("No data loaded.");
            return {userData, usertab: myParams.usertab, offertab: myParams.offertab, seenCars: seenCars.seenCar};
          }).catch(error => {
            throw new Error("Unable to load user data");
          });
        },
        element: <ProtectedRoute condition={"isConnected === true"} redirectPath={RouterPathResolver.pathHome.path}><UserSingle /></ProtectedRoute>,
      },
      {
        path: `${RouterPathResolver.pathOffers.path}`,
        loader: ({request}) => {
          return ExtractQueryInObj(request);
        },
        element: <ProtectedRoute condition={"isConnected === true"} redirectPath={RouterPathResolver.pathHome.path}><OfferTabs /></ProtectedRoute>,
        errorElement: <ErrorPage />,
      },
      {
        path: `${RouterPathResolver.pathCar_BY_ID.path}`,
        loader: async ({params, request})=> {
          const myParams = InitRequestParams(request.url);
          return {
            editable: myParams.editable,
            id: params.id!,
            promises: [
              ServiceGeneral.manageAxiosResponse(() => {return CarService.getCarByID(params.id!)}, () => logout()),
              ServiceGeneral.manageAxiosResponse(() => {return CarService.getCarPictures(params.id!)}, () => logout()),
              ServiceGeneral.manageAxiosResponse(() => {return CarService.templateData()}, () => logout()),
              ServiceGeneral.manageAxiosResponse(() => {return PricingService.getPricing(params.id!)}, () => logout())
            ]
          };
        },
        element: <ProtectedRoute condition={"isConnected === true"} redirectPath={RouterPathResolver.pathHome.path}><Car /></ProtectedRoute>,
        errorElement: <ErrorPage />,
      },
      {
        path: `${RouterPathResolver.pathCompanies.path}`,
        loader: async () => {
          return await ServiceGeneral.manageAxiosResponse( () => { return CompanyService.getAllCompanies()}, () => logout());
        },
        element: <ProtectedRoute condition={"isConnected === true"} redirectPath={RouterPathResolver.pathHome.path}><CompanyList /></ProtectedRoute>,
        errorElement: <ErrorPage />,
      },
      {
        path: `${RouterPathResolver.pathCompany_BY_ID.path}`,
        loader: async ({params, request})=> {
          const myParams = InitRequestParams(request.url);
          return await ServiceGeneral.manageAxiosResponse( () => { return CompanyService.getCompanyByID(params.id!, {...myParams})}, () => logout());
        },
        element: <ProtectedRoute condition={"isConnected === true"} redirectPath={RouterPathResolver.pathHome.path}><Company /></ProtectedRoute>,
        errorElement: <ErrorPage />,
      },
      {
        path: `${RouterPathResolver.pathCar.path}`,
        loader: async () => {
          return await ServiceGeneral.manageAxiosResponse( () => { return CarService.getCarList({perpage: 999})}, () => logout());
        },
        element: <ProtectedRoute condition={"isConnected === true"} redirectPath={RouterPathResolver.pathHome.path}><CarList /></ProtectedRoute>,
        errorElement: <ErrorPage />,
      }
    ]
  },
]);
}

export default Router;
